import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Text,
} from '@fluentui/react-components';
import { ImageMultiple20Regular, DocumentPdf24Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import type { OrderAttachment } from './AttachmentGallery';
import PhotoManagerDialog, { PHOTO_KIND, PHOTO_CATEGORIES } from './PhotoManagerDialog';

// Re-export so existing importers of PHOTO_CATEGORIES keep working.
export { PHOTO_CATEGORIES };

// How many thumbnails the compact summary previews before "+N more".
const PREVIEW_COUNT = 4;

const isPdf = (att: OrderAttachment): boolean =>
  (att.file_type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(att.name || '');

interface PhotoSectionProps {
  /** TWM order UUID — photos attach to the local order row (like notes), so
   *  this is present for any saved order whether promoted or not. */
  orderId: string | null;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('10px'),
  },
  hint: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  preview: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('6px'),
  },
  thumb: {
    width: '48px',
    height: '48px',
    objectFit: 'cover',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    display: 'block',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // PDFs can't render in an <img>; show a small document tile instead.
  thumbDoc: {
    width: '48px',
    height: '48px',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  // Trailing "+N more" tile that matches the thumbnail footprint.
  more: {
    width: '48px',
    height: '48px',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
  },
});

/**
 * Compact order-photo summary for the right sidebar: a count, a short thumbnail
 * preview, and a button that opens the full-screen PhotoManagerDialog where the
 * real upload/caption/rotate/delete work happens. Shares the gallery's TanStack
 * query key, so the preview stays live after edits in the dialog without an
 * extra fetch.
 */
const PhotoSection: React.FC<PhotoSectionProps> = ({ orderId }) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  const attachmentsQuery = useQuery<{ data: OrderAttachment[] }>({
    queryKey: ['order-attachments', orderId, PHOTO_KIND, null],
    queryFn: async () => {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId!)}/attachments?kind=${encodeURIComponent(PHOTO_KIND)}`,
      );
      if (!res.ok) throw new Error(`attachments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 60 * 1000,
  });

  if (!orderId) {
    return <div className={styles.hint}>Save the order before adding photos.</div>;
  }

  const attachments = attachmentsQuery.data?.data ?? [];
  const count = attachments.length;
  const preview = attachments.slice(0, PREVIEW_COUNT);
  const extra = count - preview.length;

  return (
    <div className={styles.root}>
      <Button
        appearance="primary"
        icon={<ImageMultiple20Regular />}
        onClick={() => setOpen(true)}
      >
        {count > 0 ? `Manage photos (${count})` : 'Add photos'}
      </Button>

      {count > 0 && (
        <div className={styles.preview}>
          {preview.map((att) =>
            att.signed_url ? (
              isPdf(att) ? (
                <div
                  key={att.id}
                  className={styles.thumbDoc}
                  role="button"
                  tabIndex={0}
                  title={att.caption || att.name}
                  onClick={() => setOpen(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}
                >
                  <DocumentPdf24Regular />
                </div>
              ) : (
                <img
                  key={att.id}
                  className={styles.thumb}
                  src={att.signed_url}
                  alt={att.caption || att.name}
                  title={att.caption || att.name}
                  onClick={() => setOpen(true)}
                />
              )
            ) : null,
          )}
          {extra > 0 && (
            <div
              className={styles.more}
              role="button"
              tabIndex={0}
              title={`${extra} more`}
              onClick={() => setOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}
            >
              +{extra}
            </div>
          )}
        </div>
      )}

      {count === 0 && !attachmentsQuery.isLoading && (
        <Text className={styles.hint}>
          No photos yet — add site, delivery, or damage photos.
        </Text>
      )}

      <PhotoManagerDialog orderId={orderId} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

export default PhotoSection;
